import * as Popover from '@radix-ui/react-popover';
import { SlidersHorizontal, X } from 'lucide-react';

import { ActiveFilterBar, FilterChip, SearchInput, TokenInput } from '@/components/filterChips';
import { Button } from '@/components/ui/button';
import { Select, SelectGroup } from '@/components/ui/select';
import type { AnalyticsUrlState } from '@/lib/urlState';
import {
  failureRecurrenceValues,
  failureSeverityValues,
  percentileValues,
  queueViewValues,
  rumVitalValues,
  runStatusValues,
  runtimeValues,
  sessionErrorValues,
  workloadSizeValues,
} from '@/lib/urlState';

export type PageFilterKey =
  | 'analysis'
  | 'failureGroup'
  | 'failureRecurrence'
  | 'failureSeverity'
  | 'percentile'
  | 'queue'
  | 'runStatus'
  | 'runtime'
  | 'search'
  | 'sessionBrowser'
  | 'sessionDevice'
  | 'sessionError'
  | 'sessionRoute'
  | 'sessionVital'
  | 'service'
  | 'source'
  | 'workerVersion'
  | 'workloadSize';

type FilterBarProperties = {
  filters: PageFilterKey[];
  mode?: 'active' | 'all' | 'trigger';
  onChange: (nextState: Partial<AnalyticsUrlState>) => void;
  state: AnalyticsUrlState;
};

const includes = (filters: PageFilterKey[], filter: PageFilterKey) => filters.includes(filter);

const sessionErrorLabel = (value: AnalyticsUrlState['sessionErrorStatus']): string => {
  if (value === 'all') return 'Any error state';
  return value === 'with' ? 'With errors' : 'Without errors';
};

// This component is the explicit rendering matrix for the small, fixed set of
// page-local analytics filters.
// eslint-disable-next-line sonarjs/cognitive-complexity
export const FilterBar = ({ filters, mode = 'all', onChange, state }: FilterBarProperties) => {
  const active = [
    includes(filters, 'search') && Boolean(state.search),
    includes(filters, 'analysis') && state.analysisTypes.length > 0,
    includes(filters, 'service') && state.serviceNames.length > 0,
    includes(filters, 'source') && state.sources.length > 0,
    includes(filters, 'runtime') && state.runtimeTypes.length > 0,
    includes(filters, 'failureGroup') && state.failureGroupKeys.length > 0,
    includes(filters, 'failureSeverity') && state.failureSeverities.length > 0,
    includes(filters, 'failureRecurrence') && state.failureRecurrence !== 'all',
    includes(filters, 'percentile') && state.percentile !== 'p95',
    includes(filters, 'queue') && state.queueView !== 'open',
    includes(filters, 'runStatus') && state.runStatuses.length > 0,
    includes(filters, 'workerVersion') && state.workerImageVersions.length > 0,
    includes(filters, 'workloadSize') && state.workloadSizeClasses.length > 0,
    includes(filters, 'sessionRoute') && state.sessionRoutes.length > 0,
    includes(filters, 'sessionBrowser') && state.sessionBrowsers.length > 0,
    includes(filters, 'sessionDevice') && state.sessionDevices.length > 0,
    includes(filters, 'sessionVital') && state.sessionVitalNames.length > 0,
    includes(filters, 'sessionError') && state.sessionErrorStatus !== 'all',
  ].filter(Boolean).length;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const resetFilters = () => {
    const reset: Partial<AnalyticsUrlState> = { runOffset: 0, sessionOffset: 0 };
    if (includes(filters, 'search')) reset.search = '';
    if (includes(filters, 'analysis')) reset.analysisTypes = [];
    if (includes(filters, 'service')) reset.serviceNames = [];
    if (includes(filters, 'source')) reset.sources = [];
    if (includes(filters, 'runtime')) reset.runtimeTypes = [];
    if (includes(filters, 'failureGroup')) reset.failureGroupKeys = [];
    if (includes(filters, 'failureSeverity')) reset.failureSeverities = [];
    if (includes(filters, 'failureRecurrence')) reset.failureRecurrence = 'all';
    if (includes(filters, 'percentile')) reset.percentile = 'p95';
    if (includes(filters, 'queue')) reset.queueView = 'open';
    if (includes(filters, 'runStatus')) reset.runStatuses = [];
    if (includes(filters, 'workerVersion')) reset.workerImageVersions = [];
    if (includes(filters, 'workloadSize')) reset.workloadSizeClasses = [];
    if (includes(filters, 'sessionRoute')) reset.sessionRoutes = [];
    if (includes(filters, 'sessionBrowser')) reset.sessionBrowsers = [];
    if (includes(filters, 'sessionDevice')) reset.sessionDevices = [];
    if (includes(filters, 'sessionVital')) reset.sessionVitalNames = [];
    if (includes(filters, 'sessionError')) reset.sessionErrorStatus = 'all';
    onChange(reset);
  };
  const filterLabel = active ? `Filters, ${active} active` : 'Filters';

  if (filters.length === 0) {
    return null;
  }

  if (mode === 'active' && !active) {
    return null;
  }

  return (
    <div className={mode === 'all' ? 'grid gap-2' : undefined}>
      <Popover.Root>
        <Popover.Trigger asChild>
          <Button
            aria-hidden={mode === 'active' ? true : undefined}
            aria-label={filterLabel}
            style={mode === 'active' ? { display: 'none' } : undefined}
            tabIndex={mode === 'active' ? -1 : undefined}
            variant="secondary"
          >
            <SlidersHorizontal aria-hidden="true" className="size-4" />
            Filters
            {active ? (
              <span className="bg-action text-raised grid size-5 place-items-center rounded-full text-[0.6875rem] font-semibold">
                {active}
              </span>
            ) : null}
          </Button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="end"
            className="border-line bg-raised text-ink shadow-raised z-50 w-[min(94vw,48rem)] rounded-lg border p-4"
            sideOffset={8}
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">Page filters</p>
                <p className="text-muted mt-1 text-xs">Only controls that affect this destination are shown.</p>
              </div>
              {active ? (
                <Button onClick={resetFilters} variant="ghost">
                  <X aria-hidden="true" className="size-4" />
                  Reset
                </Button>
              ) : null}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {includes(filters, 'search') ? (
                <label className="text-muted grid gap-1.5 text-xs font-semibold">
                  Search
                  <SearchInput onChange={(search) => onChange({ search })} placeholder="Search this destination" value={state.search} />
                </label>
              ) : null}
              {includes(filters, 'analysis') ? (
                <label className="text-muted grid gap-1.5 text-xs font-semibold">
                  Analysis types
                  <TokenInput
                    onChange={(analysisTypes) => onChange({ analysisTypes, runOffset: 0 })}
                    placeholder="Add an analysis type"
                    values={state.analysisTypes}
                  />
                </label>
              ) : null}
              {includes(filters, 'service') ? (
                <label className="text-muted grid gap-1.5 text-xs font-semibold">
                  Services
                  <TokenInput
                    onChange={(serviceNames) => onChange({ runOffset: 0, serviceNames })}
                    placeholder="Add a service"
                    values={state.serviceNames}
                  />
                </label>
              ) : null}
              {includes(filters, 'source') ? (
                <label className="text-muted grid gap-1.5 text-xs font-semibold">
                  Sources
                  <TokenInput onChange={(sources) => onChange({ sources })} placeholder="Add a source" values={state.sources} />
                </label>
              ) : null}
              {includes(filters, 'failureGroup') ? (
                <label className="text-muted grid gap-1.5 text-xs font-semibold">
                  Failure groups
                  <TokenInput
                    onChange={(failureGroupKeys) => onChange({ failureGroupKeys })}
                    placeholder="Add a group key"
                    values={state.failureGroupKeys}
                  />
                </label>
              ) : null}
              {includes(filters, 'workerVersion') ? (
                <label className="text-muted grid gap-1.5 text-xs font-semibold">
                  Worker versions
                  <TokenInput
                    onChange={(workerImageVersions) => onChange({ runOffset: 0, workerImageVersions })}
                    placeholder="Add a worker version"
                    values={state.workerImageVersions}
                  />
                </label>
              ) : null}
              {includes(filters, 'sessionRoute') ? (
                <label className="text-muted grid gap-1.5 text-xs font-semibold">
                  Routes
                  <TokenInput
                    onChange={(sessionRoutes) => onChange({ sessionOffset: 0, sessionRoutes })}
                    placeholder="Add an exact route"
                    values={state.sessionRoutes}
                  />
                </label>
              ) : null}
              {includes(filters, 'sessionBrowser') ? (
                <label className="text-muted grid gap-1.5 text-xs font-semibold">
                  Browsers
                  <TokenInput
                    onChange={(sessionBrowsers) => onChange({ sessionBrowsers, sessionOffset: 0 })}
                    placeholder="Add a browser"
                    values={state.sessionBrowsers}
                  />
                </label>
              ) : null}
              {includes(filters, 'sessionDevice') ? (
                <label className="text-muted grid gap-1.5 text-xs font-semibold">
                  Devices
                  <TokenInput
                    onChange={(sessionDevices) => onChange({ sessionDevices, sessionOffset: 0 })}
                    placeholder="Add a device"
                    values={state.sessionDevices}
                  />
                </label>
              ) : null}
              <SelectGroup>
                {includes(filters, 'runtime') ? (
                  <Select
                    label="Runtime"
                    onValueChange={(value) =>
                      onChange({
                        runOffset: 0,
                        runtimeTypes: value === 'all' ? [] : [value as AnalyticsUrlState['runtimeTypes'][number]],
                      })
                    }
                    options={[
                      { label: 'All runtimes', value: 'all' },
                      ...runtimeValues.map((runtimeType) => ({ label: runtimeType, value: runtimeType })),
                    ]}
                    value={state.runtimeTypes[0] ?? 'all'}
                  />
                ) : null}
                {includes(filters, 'failureSeverity') ? (
                  <Select
                    label="Severity"
                    onValueChange={(value) =>
                      onChange({
                        failureSeverities: value === 'all' ? [] : [value as AnalyticsUrlState['failureSeverities'][number]],
                      })
                    }
                    options={[
                      { label: 'All severities', value: 'all' },
                      ...failureSeverityValues.map((severity) => ({ label: severity, value: severity })),
                    ]}
                    value={state.failureSeverities[0] ?? 'all'}
                  />
                ) : null}
                {includes(filters, 'failureRecurrence') ? (
                  <Select
                    label="Recurrence"
                    onValueChange={(value) => onChange({ failureRecurrence: value as AnalyticsUrlState['failureRecurrence'] })}
                    options={failureRecurrenceValues.map((recurrence) => ({
                      label: recurrence === 'all' ? 'Any recurrence' : recurrence,
                      value: recurrence,
                    }))}
                    value={state.failureRecurrence}
                  />
                ) : null}
                {includes(filters, 'percentile') ? (
                  <Select
                    label="Percentile"
                    onValueChange={(value) => onChange({ percentile: value as AnalyticsUrlState['percentile'] })}
                    options={percentileValues.map((percentile) => ({ label: percentile.toUpperCase(), value: percentile }))}
                    value={state.percentile}
                  />
                ) : null}
                {includes(filters, 'queue') ? (
                  <Select
                    label="Queue"
                    onValueChange={(value) => onChange({ queueView: value as AnalyticsUrlState['queueView'] })}
                    options={queueViewValues.map((queueView) => ({ label: queueView, value: queueView }))}
                    value={state.queueView}
                  />
                ) : null}
                {includes(filters, 'runStatus') ? (
                  <Select
                    label="Run status"
                    onValueChange={(value) =>
                      onChange({
                        runOffset: 0,
                        runStatuses: value === 'all' ? [] : [value as AnalyticsUrlState['runStatuses'][number]],
                      })
                    }
                    options={[
                      { label: 'All run statuses', value: 'all' },
                      ...runStatusValues.map((status) => ({ label: status, value: status })),
                    ]}
                    value={state.runStatuses[0] ?? 'all'}
                  />
                ) : null}
                {includes(filters, 'workloadSize') ? (
                  <Select
                    label="Workload size"
                    onValueChange={(value) =>
                      onChange({
                        runOffset: 0,
                        workloadSizeClasses: value === 'all' ? [] : [value as AnalyticsUrlState['workloadSizeClasses'][number]],
                      })
                    }
                    options={[
                      { label: 'All workload sizes', value: 'all' },
                      ...workloadSizeValues.map((size) => ({ label: size, value: size })),
                    ]}
                    value={state.workloadSizeClasses[0] ?? 'all'}
                  />
                ) : null}
                {includes(filters, 'sessionVital') ? (
                  <Select
                    label="Web vital"
                    onValueChange={(value) =>
                      onChange({
                        sessionOffset: 0,
                        sessionVitalNames: value === 'all' ? [] : [value as AnalyticsUrlState['sessionVitalNames'][number]],
                      })
                    }
                    options={[{ label: 'All vitals', value: 'all' }, ...rumVitalValues.map((vital) => ({ label: vital, value: vital }))]}
                    value={state.sessionVitalNames[0] ?? 'all'}
                  />
                ) : null}
                {includes(filters, 'sessionError') ? (
                  <Select
                    label="Client errors"
                    onValueChange={(value) =>
                      onChange({
                        sessionErrorStatus: value as AnalyticsUrlState['sessionErrorStatus'],
                        sessionOffset: 0,
                      })
                    }
                    options={sessionErrorValues.map((value) => ({
                      label: sessionErrorLabel(value),
                      value,
                    }))}
                    value={state.sessionErrorStatus}
                  />
                ) : null}
              </SelectGroup>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {mode !== 'trigger' && active ? (
        <ActiveFilterBar>
          {includes(filters, 'search') && state.search ? (
            <FilterChip onRemove={() => onChange({ search: '' })}>Search: {state.search}</FilterChip>
          ) : null}
          {includes(filters, 'analysis')
            ? state.analysisTypes.map((analysisType) => (
                <FilterChip
                  key={analysisType}
                  onRemove={() =>
                    onChange({
                      analysisTypes: state.analysisTypes.filter((value) => value !== analysisType),
                      runOffset: 0,
                    })
                  }
                  removeLabel={`Remove analysis ${analysisType}`}
                >
                  Analysis: {analysisType}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'service')
            ? state.serviceNames.map((serviceName) => (
                <FilterChip
                  key={serviceName}
                  onRemove={() =>
                    onChange({
                      runOffset: 0,
                      serviceNames: state.serviceNames.filter((value) => value !== serviceName),
                    })
                  }
                  removeLabel={`Remove service ${serviceName}`}
                >
                  Service: {serviceName}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'source')
            ? state.sources.map((source) => (
                <FilterChip
                  key={source}
                  onRemove={() => onChange({ sources: state.sources.filter((value) => value !== source) })}
                  removeLabel={`Remove source ${source}`}
                >
                  Source: {source}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'runtime')
            ? state.runtimeTypes.map((runtimeType) => (
                <FilterChip
                  key={runtimeType}
                  onRemove={() =>
                    onChange({
                      runOffset: 0,
                      runtimeTypes: state.runtimeTypes.filter((value) => value !== runtimeType),
                    })
                  }
                  removeLabel={`Remove runtime ${runtimeType}`}
                >
                  Runtime: {runtimeType}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'failureGroup')
            ? state.failureGroupKeys.map((groupKey) => (
                <FilterChip
                  key={groupKey}
                  onRemove={() => onChange({ failureGroupKeys: state.failureGroupKeys.filter((value) => value !== groupKey) })}
                  removeLabel={`Remove failure group ${groupKey}`}
                >
                  Group: {groupKey}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'failureSeverity')
            ? state.failureSeverities.map((severity) => (
                <FilterChip
                  key={severity}
                  onRemove={() => onChange({ failureSeverities: state.failureSeverities.filter((value) => value !== severity) })}
                  removeLabel={`Remove severity ${severity}`}
                >
                  Severity: {severity}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'failureRecurrence') && state.failureRecurrence !== 'all' ? (
            <FilterChip
              onRemove={() => onChange({ failureRecurrence: 'all' })}
              removeLabel={`Remove recurrence ${state.failureRecurrence}`}
            >
              Recurrence: {state.failureRecurrence}
            </FilterChip>
          ) : null}
          {includes(filters, 'percentile') && state.percentile !== 'p95' ? (
            <FilterChip onRemove={() => onChange({ percentile: 'p95' })}>Percentile: {state.percentile.toUpperCase()}</FilterChip>
          ) : null}
          {includes(filters, 'queue') && state.queueView !== 'open' ? (
            <FilterChip onRemove={() => onChange({ queueView: 'open' })}>Queue: {state.queueView}</FilterChip>
          ) : null}
          {includes(filters, 'runStatus')
            ? state.runStatuses.map((status) => (
                <FilterChip
                  key={status}
                  onRemove={() => onChange({ runOffset: 0, runStatuses: state.runStatuses.filter((value) => value !== status) })}
                  removeLabel={`Remove run status ${status}`}
                >
                  Run status: {status}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'workerVersion')
            ? state.workerImageVersions.map((version) => (
                <FilterChip
                  key={version}
                  onRemove={() =>
                    onChange({
                      runOffset: 0,
                      workerImageVersions: state.workerImageVersions.filter((value) => value !== version),
                    })
                  }
                  removeLabel={`Remove worker version ${version}`}
                >
                  Worker: {version}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'workloadSize')
            ? state.workloadSizeClasses.map((size) => (
                <FilterChip
                  key={size}
                  onRemove={() =>
                    onChange({
                      runOffset: 0,
                      workloadSizeClasses: state.workloadSizeClasses.filter((value) => value !== size),
                    })
                  }
                  removeLabel={`Remove workload size ${size}`}
                >
                  Workload: {size}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'sessionRoute')
            ? state.sessionRoutes.map((route) => (
                <FilterChip
                  key={route}
                  onRemove={() =>
                    onChange({
                      sessionOffset: 0,
                      sessionRoutes: state.sessionRoutes.filter((value) => value !== route),
                    })
                  }
                  removeLabel={`Remove route ${route}`}
                >
                  Route: {route}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'sessionBrowser')
            ? state.sessionBrowsers.map((browser) => (
                <FilterChip
                  key={browser}
                  onRemove={() =>
                    onChange({
                      sessionBrowsers: state.sessionBrowsers.filter((value) => value !== browser),
                      sessionOffset: 0,
                    })
                  }
                  removeLabel={`Remove browser ${browser}`}
                >
                  Browser: {browser}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'sessionDevice')
            ? state.sessionDevices.map((device) => (
                <FilterChip
                  key={device}
                  onRemove={() =>
                    onChange({
                      sessionDevices: state.sessionDevices.filter((value) => value !== device),
                      sessionOffset: 0,
                    })
                  }
                  removeLabel={`Remove device ${device}`}
                >
                  Device: {device}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'sessionVital')
            ? state.sessionVitalNames.map((vital) => (
                <FilterChip
                  key={vital}
                  onRemove={() =>
                    onChange({
                      sessionOffset: 0,
                      sessionVitalNames: state.sessionVitalNames.filter((value) => value !== vital),
                    })
                  }
                  removeLabel={`Remove vital ${vital}`}
                >
                  Vital: {vital}
                </FilterChip>
              ))
            : null}
          {includes(filters, 'sessionError') && state.sessionErrorStatus !== 'all' ? (
            <FilterChip
              onRemove={() => onChange({ sessionErrorStatus: 'all', sessionOffset: 0 })}
              removeLabel={`Remove client error filter ${state.sessionErrorStatus}`}
            >
              Client errors: {state.sessionErrorStatus}
            </FilterChip>
          ) : null}
          <Button className="ml-auto" onClick={resetFilters} variant="ghost">
            Clear
          </Button>
        </ActiveFilterBar>
      ) : null}
    </div>
  );
};
